/**
 * Campaign Design Studio — preset data + preset-merge logic.
 *
 * Extracted from AdminCampaignEditor.tsx (2026-05-21) so the pure
 * preset logic is unit-testable without pulling the 2000-line React
 * component (and its react-router / lucide imports) into a node test.
 * This module is JSX-free and side-effect-free.
 *
 * Pinned by web/src/__tests__/campaignPresetMerge.test.ts and
 * tests/gate/test_campaigns_design_studio.py.
 */

// ================================================================
// Style Presets — curated looks per campaign type
// ================================================================

export interface StylePreset {
  id: string;
  label: string;
  description: string;
  config: Record<string, any>;
}

// ─── Announcement style presets ──────────────────────────────────────
//
// Default (first in list) = Cedar Classic, the dark-cedar premium look
// that's used for ~all real announcements today. White-background option
// is kept as "Cedar Light" for cases that need a brighter feel.
//
// "Celebration" (amber/gold) removed 2026-05-21 — off-palette; was
// rarely used and reintroducing it would require a Matt-approved
// color refresh. Stream Teal + Subtle Green kept.
//
// "Cedar Gradient" (NEW 2026-05-21) — matches the LoginScreen hero
// gradient (cedarInk → darkCedar → darkCedar → cedarInk, horizontal).
// Adds `background_gradient` on display_config. Renderer reads this
// in preference to `background_color` when present and wraps the
// modal in an expo-linear-gradient. `background_color` stays set
// (darkCedar) as a degradation if the gradient ever fails to mount.
//
// Sprint: Campaigns Design Studio polish (2026-05-21).
// Pinned by tests/gate/test_campaigns_design_studio.py.
export const ANNOUNCEMENT_PRESETS: StylePreset[] = [
  {
    id: 'cedar_classic', label: 'Cedar Classic', description: 'Dark cedar background, white text — premium default for major announcements',
    config: { background_color: '#113C2B', accent_color: '#5FA287', title_color: '#FFFFFF', body_color: '#A8D1BF', title_size: 28, body_size: 16, icon_size: 48, border_radius: 12, content_align: 'center', template: 'takeover' },
  },
  {
    id: 'cedar_gradient', label: 'Cedar Gradient', description: 'Hero gradient matching the login page — cedar ink → dark cedar → cedar ink',
    config: {
      background_color: '#113C2B',
      background_gradient: {
        colors: ['#0B1614', '#113C2B', '#113C2B', '#0B1614'],
        locations: [0, 0.14, 0.86, 1],
        start: { x: 0, y: 0 },
        end: { x: 1, y: 0 },
      },
      accent_color: '#5FA287',
      title_color: '#FFFFFF',
      body_color: '#A8D1BF',
      title_size: 32,
      body_size: 18,
      icon_size: 64,
      border_radius: 12,
      content_align: 'center',
      template: 'takeover',
    },
  },
  {
    id: 'cedar_light', label: 'Cedar Light', description: 'White background, cedar green accents — clean and professional',
    config: { background_color: '#FFFFFF', accent_color: '#1F6C4F', title_color: '#111827', body_color: '#6B7280', title_size: 28, body_size: 17, icon_size: 56, border_radius: 12, content_align: 'center', template: 'takeover' },
  },
  {
    id: 'subtle_green', label: 'Subtle Green', description: 'Soft cedar tint background — warm and inviting',
    config: { background_color: '#E8F2ED', accent_color: '#1F6C4F', title_color: '#113C2B', body_color: '#374151', title_size: 26, body_size: 16, icon_size: 48, border_radius: 12, content_align: 'center', template: 'takeover' },
  },
  {
    id: 'stream_teal', label: 'Stream Teal', description: 'Cedar Stream accent — fresh, active energy',
    config: { background_color: '#F0FDFA', accent_color: '#42A5A5', title_color: '#134E4A', body_color: '#374151', title_size: 28, body_size: 17, icon_size: 56, border_radius: 12, content_align: 'center', template: 'takeover' },
  },
];

// 2026-05-22: aligned to the announcement palette (Mission 10). Cedar Classic
// (dark cedar, white text — same #113C2B / #5FA287 / #FFFFFF / #A8D1BF as the
// announcement) leads the list so a contextual notice can match the
// announcement's premium look. Every preset now sets `background_color` so the
// theme-aware native component (CampaignContextualNotice) derives readable text
// via isBackgroundDark and can never render dark-on-dark.
export const CONTEXTUAL_NOTICE_PRESETS: StylePreset[] = [
  {
    id: 'cedar_classic', label: 'Cedar Classic', description: 'Dark cedar sheet, white text — matches the announcement default',
    config: { background_color: '#113C2B', accent_color: '#5FA287', title_color: '#FFFFFF', body_color: '#A8D1BF', title_size: 18, body_size: 14, icon_size: 32, height: 'auto' },
  },
  {
    id: 'default_notice', label: 'Cedar Light', description: 'Clean white bottom sheet — informational',
    config: { background_color: '#FFFFFF', accent_color: '#1F6C4F', title_color: '#111827', body_color: '#6B7280', title_size: 18, body_size: 14, icon_size: 32, height: 'auto' },
  },
  {
    id: 'coming_soon', label: 'Coming Soon', description: 'Subtle green tint — feature anticipation',
    config: { background_color: '#E8F2ED', accent_color: '#1F6C4F', title_color: '#113C2B', body_color: '#374151', title_size: 20, body_size: 15, icon_size: 36, height: 'auto' },
  },
  {
    id: 'attention', label: 'Attention', description: 'Amber accent — important but not alarming',
    config: { background_color: '#FFFFFF', accent_color: '#D97706', title_color: '#92400E', body_color: '#78350F', title_size: 18, body_size: 14, icon_size: 32, height: 'auto' },
  },
];

export const SURVEY_PRESETS: StylePreset[] = [
  {
    id: 'team_classic', label: 'Team Classic', description: 'Cedar green team branding — standard survey',
    config: { background_color: '#E8F2ED', accent_color: '#1F6C4F', title_color: '#113C2B', body_color: '#374151', title_size: 16, body_size: 14, icon_size: 36, team_badge_color: '#1F6C4F' },
  },
  {
    id: 'team_warm', label: 'Team Warm', description: 'Warm limestone — feels casual and friendly',
    config: { background_color: '#F3F3F3', accent_color: '#1F6C4F', title_color: '#111827', body_color: '#6B7280', title_size: 16, body_size: 14, icon_size: 36, team_badge_color: '#374151' },
  },
];

export const CEDAR_CHAT_PRESETS: StylePreset[] = [
  {
    id: 'default_chat', label: 'Default Cedar', description: 'Standard cedar chat bubble styling',
    config: { accent_color: '#1F6C4F' },
  },
];

export function getPresetsForType(type: string): StylePreset[] {
  switch (type) {
    case 'announcement': return ANNOUNCEMENT_PRESETS;
    case 'contextual_notice': return CONTEXTUAL_NOTICE_PRESETS;
    case 'survey': return SURVEY_PRESETS;
    case 'cedar_chat': return CEDAR_CHAT_PRESETS;
    default: return [];
  }
}

// ── Preset application (exported for unit testing) ──────────────────────────
//
// Bug (2026-05-21): selecting Cedar Gradient set `background_gradient` on the
// step's display_config, but switching to any solid-background preset did NOT
// clear it — the old code did a naive `{ ...display_config, ...preset.config }`
// shallow merge, and only the gradient preset defines a `background_gradient`
// key, so nothing overwrote it. The renderer prefers `background_gradient`
// over `background_color`, so the gradient "stuck" in the preview (and would
// have persisted to the DB on save). One-way trap: you could get into the
// gradient look but never back out via the preset picker.
//
// Fix: before applying a preset, strip every key that ANY preset of this
// campaign type can set, then layer the chosen preset on top. This makes a
// preset switch a clean slate for all preset-controlled fields, while leaving
// non-preset display_config keys (title_align/body_align/cta_align,
// drawer_height, dismiss_behavior — none of which appear in a preset) intact.
// Self-maintaining: a future preset key is automatically covered.
//
// Pinned by web/src/pages/__tests__/applyPresetToDisplayConfig.test.ts and
// tests/gate/test_campaigns_design_studio.py.

/** Union of every key any preset for `campaignType` can define. */
export function presetManagedKeys(campaignType: string): string[] {
  const keys = new Set<string>();
  for (const preset of getPresetsForType(campaignType)) {
    for (const key of Object.keys(preset.config)) keys.add(key);
  }
  return [...keys];
}

/**
 * Produce the next display_config when `preset` is applied on top of
 * `displayConfig`. Preset-managed keys are cleared first so no key from a
 * previously-applied preset (e.g. Cedar Gradient's background_gradient) can
 * leak into a preset that doesn't define it. Pure — no React state.
 */
export function applyPresetToDisplayConfig(
  displayConfig: Record<string, any>,
  preset: StylePreset,
  campaignType: string,
): Record<string, any> {
  const cleaned: Record<string, any> = { ...(displayConfig || {}) };
  for (const key of presetManagedKeys(campaignType)) {
    delete cleaned[key];
  }
  return { ...cleaned, ...preset.config, preset_id: preset.id };
}
